export const meta = {
  id: '43-verb-classes-irregulars',
  order: 43,
  titleFi: 'Verbiluokat, perfektivartalot ja epäsäännölliset verbit',
  titleEn: 'Verb classes, perfect stems and irregular verbs',
  lemmaIds: [
    'gaomagon', 'derēbagon', 'verdagon', 'heghagon', 'leghagon', 'tyvagon', 'ūñagon',
    'siñagon', 'isahagon', 'dōruragon', 'vēttīlaksir',
  ],
  exampleIds: [
    'got-s3e05-002', 'got-s3e10-003', 'got-s6e01-002', 'got-s5e04-003', 'hotd-s1e05-007',
    'hotd-s3e05-010', 'ex-7b6eb4f655d8', 'hotd-s3e05-007', 'hotd-s1e05-008', 'hotd-s2e04-008',
    'got-s7e04-001', 'hotd-s1e04-002', 'got-s5e03-009',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Conjugation',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Tables',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Reference_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Derivational_Affixes',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=Sagon',
    'https://wiki.languageinvention.com/index.php?title=Ilagon',
    'https://wiki.languageinvention.com/index.php?title=Jagon',
    'https://wiki.languageinvention.com/index.php?title=Emagon',
    'https://wiki.languageinvention.com/index.php?title=Sahagon',
    'https://wiki.languageinvention.com/index.php?title=Gaomagon',
    'https://dothraki.com/2013/06/some-more-high-valyrian-inflection/',
    'https://wiki.languageinvention.com/index.php?title=Jaelas',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['verb-classes'],
  showLines: ['got-s3e05-002', 'got-s3e10-003', 'got-s6e01-002', 'got-s5e04-003', 'hotd-s1e05-007', 'hotd-s3e05-010', 'hotd-s3e05-007', 'hotd-s1e05-008', 'hotd-s2e04-008', 'got-s7e04-001', 'hotd-s1e04-002', 'got-s5e03-009'],
  pronunciation: { focus: 'long-vowels', words: ['derēbagon', 'ūñagon', 'dōruragon', 'vēttīlaksir', 'iōremagon'], lines: ['got-s3e05-002', 'got-s3e10-003', 'got-s6e01-002'] },
  letter: {
    fi: 'Kirjoita lyhyt kertomus epäsäännöllisillä verbeillä.',
    en: 'Write a short story with irregular verbs.',
  },
  scene: 'hotd-s3e05-1',
}

# Verbiluokat, perfektivartalot ja epäsäännölliset verbit

Tämä on kurssin työkalutunti. Kun kirjoitatte toisillenne, törmäätte jatkuvasti verbeihin, joita
Duolingo ei opettanut. Sovelluksen sanakirja näyttää jokaisen verbin taivutuksen, mutta on paljon
mukavampaa, kun osaa arvata. Tällä oppitunnilla käydään läpi kolme asiaa: (1) mistä tietää
yksikön 3. persoonan päätteen, (2) miten perfektivartalo yleensä muodostetaan ja (3) kuusi
tärkeintä epäsäännöllistä verbiä kokonaisuutena.

## Verbiluokat

Wikin viitekieliopin mukaan verbit jakautuvat <Term id="luokka">kahteen pääryhmään</Term> vartalon viimeisen äänteen mukaan:

- **konsonanttivartaloiset**: likvidat (*l, r*), nasaalit (*m, n*), soinnilliset klusiilit (*b, d, g*),
  soinnittomat klusiilit (*p, t, k, q*), palataalit (*ñ, lj*) sekä frikatiivit ja konsonanttiyhtymät;
- **vokaalivartaloiset**: *a-, e-, i-, o-* ja *u*-vartalot.

Jako on tuttu jo Duolingon preesensosiosta (<Hv>urnen</Hv> vs. <Hv>rȳban</Hv>). Uutta on se,
että konsonantin laatu ratkaisee kaksi hankalaa asiaa: yksikön 3. persoonan päätteen ja
perfektivartalon.

## Yksikön 3. persoona: *-za*, *-sa* vai *-as*?

Duolingo sanoi vain, että päätteet vaihtelevat ja ne pitää opetella. Wikin taivutussivulla on
niihin nyrkkisäännöt. Historiallisesti alkuperäinen pääte oli *-s*, josta tuli *-za* ja osassa verbejä
*-as*. Klassisessa korkeavalyriassa *-as* oli jo yhtä yleinen, ja myöhemmässä astaporinvalyriassa
lähes ainoa.

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Vartalon loppu</th><th>Pääte</th><th>Esimerkkejä</th></tr></thead>
  <tbody>
    <tr><td><Hv>l</Hv>, <Hv>r</Hv> (likvida)</td><td><Hv>-za</Hv> pakollinen</td><td><Hv l="jaelagon">jaelza</Hv>, <Hv>dohaerza</Hv>, <Hv>zālza</Hv></td></tr>
    <tr><td><Hv>p</Hv>, <Hv>t</Hv>, <Hv>k</Hv>, <Hv>q</Hv></td><td><Hv>-sa</Hv> tavallinen (<Hv>-as</Hv> epästandardi)</td><td><Hv>epsa</Hv> (<Hv>epas</Hv>)</td></tr>
    <tr><td><Hv>m</Hv>, <Hv>n</Hv></td><td><Hv>-as</Hv> yleensä; muutamalla <Hv>-za</Hv> ja pitkä vokaali</td><td><Hv>lenas</Hv> / <Hv>lēza</Hv>, <Hv>ēza</Hv>, <Hv>gōza</Hv></td></tr>
    <tr><td><Hv>b</Hv>, <Hv>d</Hv>, <Hv>g</Hv></td><td><Hv>-as</Hv> tavallinen (<Hv>-za</Hv> vanhempi, Targaryenien suosima; wikin taulukossa ”epästandardi”)</td><td><Hv>derēbas</Hv> (<Hv>derēbza</Hv>)</td></tr>
    <tr><td>frikatiivi tai yhtymä</td><td><Hv>-as</Hv> pakollinen</td><td><Hv>pāsas</Hv>, <Hv l="verdagon">verdas</Hv></td></tr>
    <tr><td><Hv>ñ</Hv>, <Hv>lj</Hv></td><td><Hv>-as</Hv>, kuten yhtymä</td><td><Hv>morghūljas</Hv></td></tr>
  </tbody>
</table>
</div>

Palataalit menettävät liudennuksensa päätteen *i*:n edellä (<Hv>morghūlis</Hv>), ja siksi muoto
näyttää samalta kuin aoristi (<LessonLink n={38}>oppitunti 38</LessonLink>).

### Daenerysin vanhahtava *-zi*

Taivutussivu huomauttaa, että Daenerys käyttää joskus ”epästandardia” *-za/-zi*-päätettä
soinnillisen klusiilin perässä:

<Line id="got-s3e05-002" />

Hän sanoo <Hv>iderēbzi</Hv> eikä <Hv>iderēbis</Hv>. Peterson selitti (IRC 2014, wikissä lainattuna), että
Targaryenit ovat vanhan Valyrian perinteen vartijoita ja heidän murteensa on vanhoillisempi. Silti
Daenerys ei ole johdonmukainen – samassa sarjassa hän sanoo <Hv fi="kuuluu">sytilības</Hv>:

<Line id="got-s3e10-003" />

<Note>
**Ristiriita wikissä:** taivutussivun mukaan likvidan perässä *-za* on **pakollinen**, mutta
Varys sanoo <LessonLink n={39}>oppitunnilla 39</LessonLink> nähdyssä repliikissä <Hv l="jaelagon" fi="haluaa">jaelas</Hv> (ei <Hv l="jaelagon">jaelza</Hv>). Wikin sanakirja merkitsee muodon *jaelas*
epästandardiksi (’nonstandard form of jaelza’), ja samassa repliikissä on toinenkin poikkeama, *gēlȳri*. Sääntö pätee.

<Line id="got-s6e01-002" />
</Note>

## Perfektivartalo: nyrkkisäännöt

Taivutussivu neuvoo opettelemaan perfektivartalon jokaisen verbin kohdalla erikseen, koska se on
niin arvaamaton. Säännönmukaisuuksia kuitenkin on – sivun omin sanoin ”taipumuksia, ei ehdottomia
sääntöjä”. Tässä ne yksikön 1. persoonan muodoin; jokainen on tarkistettu sovelluksen
taivutustaulukoista:

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Vartalo päättyy</th><th>Perfekti</th><th>Esimerkkejä</th></tr></thead>
  <tbody>
    <tr><td>vokaaliin</td><td><Hv>+t</Hv></td><td><Hv>pryjatan</Hv>, <Hv>angotan</Hv> (mutta <Hv>mastan</Hv>)</td></tr>
    <tr><td>konsonanttiyhtymään</td><td><Hv>-etan</Hv></td><td><Hv>vestretan</Hv> (mutta <Hv>vēttan</Hv>, <Hv>umptan</Hv>)</td></tr>
    <tr><td>nasaaliin</td><td><Hv>-ntan</Hv></td><td><Hv>gōntan</Hv>, <Hv>gūrēntan</Hv> (mutta <Hv>emagon</Hv>-ryhmä: <Hv>ēdan</Hv>)</td></tr>
    <tr><td>likvidaan</td><td><Hv>-tan</Hv></td><td><Hv>dohaertan</Hv>, <Hv>morghūltan</Hv> (mutta <Hv>jeldan</Hv>)</td></tr>
    <tr><td><Hv>t</Hv>, <Hv>d</Hv></td><td><Hv>-ttan</Hv></td><td><Hv>iprattan</Hv>, <Hv>vāettan</Hv></td></tr>
    <tr><td><Hv>k</Hv>, <Hv>q</Hv></td><td><Hv>-ttan</Hv></td><td><Hv>jehittan</Hv>, <Hv>sittan</Hv>, <Hv>rattan</Hv></td></tr>
    <tr><td><Hv>b</Hv></td><td><Hv>-ptan</Hv></td><td><Hv>derēptan</Hv>, <Hv>ryptan</Hv></td></tr>
    <tr><td><Hv>s</Hv>, <Hv>z</Hv></td><td><Hv>-stan</Hv></td><td><Hv>pāstan</Hv>, <Hv>brōstan</Hv></td></tr>
    <tr><td><Hv>gh</Hv>, <Hv>j</Hv>, <Hv>v</Hv></td><td><Hv>-dan</Hv> + pitkä vokaali</td><td><Hv>lēdan</Hv>, <Hv>tȳdan</Hv> (mutta <Hv>riddan</Hv>)</td></tr>
  </tbody>
</table>
</div>

Logiikka on äänteellinen: perfektin *t* sulautuu edeltävään konsonanttiin (*d + t → tt*, *b + t → pt*,
*z + t → st*). Suomessa sama ilmiö näkyy *-nut*-partisiipissa: *tul-* + *-nut* → *tullut*,
*pur-* + *-nut* → *purrut*, *pes-* + *-nut* → *pessyt*.

<Note>
**Vokaalien pituudet vaihtelevat.** Taivutussivulla on useita muotoja, joissa vokaalin pituus
poikkeaa wikin sanakirja-artikkelien taivutustaulukoista: sivulla <Hv l="jaelagon" k="perfect.1.sg">jēldan</Hv>, <Hv l="māzigon" k="perfect.1.sg">māstan</Hv>, <Hv l="verdagon" k="perfect.1.sg">vettan</Hv>,
<Hv l="rȳbagon" k="perfect.1.sg">rȳptan</Hv> ja <Hv l="lenagon" k="present.3.sg">lēnas</Hv>, taulukoissa <Hv>jeldan</Hv>, <Hv>mastan</Hv>, <Hv>vēttan</Hv>,
<Hv>ryptan</Hv> ja <Hv>lenas</Hv>. Sovellus noudattaa taulukoita, ja Peterson kirjoittaa itse samoin:
<Hv>mastan</Hv>, <Hv>vēttan</Hv> ja <Hv>ryptan</Hv> (dothraki.com 16.6.2013). Duolingo on samaa mieltä
muodosta <Hv>ryptan</Hv>: ”pitkä ȳ lyhenee”. Muodot <Hv>jeldan</Hv> ja <Hv>lenas</Hv> ovat taulukoiden
muotoja, joille Petersonin vahvistusta ei ole.
</Note>

## Kuusi epäsäännöllistä verbiä

Wikin viitekielioppi luettelee epäsäännöllisiksi verbit <Hv>emagon</Hv>, <Hv>gaomagon</Hv>, <Hv>sagon</Hv>,
<Hv>jagon</Hv>, <Hv>ilagon</Hv> ja <Hv fi="pakottaa, saada tekemään">sahagon</Hv>. Duolingo opetti niistä vain osia; nyt nähdään kokonaisuus.

### *sagon* ’olla’

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th></th><th>vartalo</th><th>esimerkki</th></tr></thead>
  <tbody>
    <tr><td>preesens</td><td><Hv>iks-</Hv> / <Hv>iss-</Hv></td><td><Hv>iksan</Hv>, <Hv l="sagon">issa</Hv>, <Hv>issi</Hv></td></tr>
    <tr><td>futuuri</td><td><Hv>kes-</Hv></td><td><Hv>kesan</Hv>, <Hv l="sagon">kessa</Hv></td></tr>
    <tr><td>imperfekti</td><td><Hv>īl-</Hv></td><td><Hv l="sagon">īlen</Hv>, <Hv l="sagon">īles</Hv></td></tr>
    <tr><td>perfekti, pluskvamperfekti, mennyt toistuva</td><td><Hv>ist-</Hv></td><td><Hv l="sagon">istan</Hv>, <Hv l="sagon">isten</Hv>, <Hv l="sagon">istin</Hv></td></tr>
    <tr><td>aoristi</td><td><Hv>iks-</Hv> / <Hv>s-</Hv></td><td><Hv>iksin</Hv>, <Hv>sia</Hv>, <Hv>iksis</Hv></td></tr>
  </tbody>
</table>
</div>

<Paradigm lemma="sagon" filter="indicative.active.imperfect" />

Imperfekti on sarjoissa yleinen. Daenerys vangista (<LessonLink n={40}>oppitunti 40</LessonLink>): <Hv glossed>Īlva ozgūroty īles</Hv>
’hän oli vankimme’.

<Line id="got-s5e04-003" />

Ja Rhaenyra Daemonille, imperfektin subjunktiivilla:

<Line id="hotd-s1e05-007" />

<Interlinear hv="Kesir ondurilla īl|os daor?" gloss="tämä-NOM tarkoitus-NOM olla-IPFV.SBJV.3SG ei" fi="Eikö tämä ollut tarkoituksesi?" />

### *jagon* ’mennä’

Preesens on lyhyt ja epäsäännöllinen: <Hv>jān</Hv>, <Hv>jā</Hv>, <Hv>is</Hv>, <Hv>jī</Hv>,
<Hv>jāt</Hv>, <Hv>isi</Hv>. Futuurin ja imperfektin vartalo on *īl-* (<Hv glossed>īnna</Hv> ’menen’,
<Hv glossed>īles</Hv> ’oli menossa’), perfektin *ist-* ja menneen toistuvan *id-* (<Hv glossed>idin</Hv> ’minulla
oli tapana mennä’).

<Example id="ex-7b6eb4f655d8" />

<Note>
**Sekaantumisvaara.** <Hv>Sagon</Hv> ja <Hv>jagon</Hv> ovat samannäköisiä sekä perfektissä
(<Hv glossed>istas</Hv> ’on ollut / on mennyt’) että imperfektissä (<Hv glossed>īles</Hv> ’oli / oli menossa’). Duolingo
mainitsi perfektin; imperfektien samannäköisyys näkyy taivutussivun taulukoista. Asiayhteys
ratkaisee: paikanmääre ja liikkeen suunta (datiivi ’jonnekin’) viittaavat menemiseen.
</Note>

### *ilagon* ’maata, olla jossakin’

Duolingosta tiedät, että kun kerrotaan, missä jokin on, ’olla’ on <Hv>ilagon</Hv> eikä <Hv>sagon</Hv>.
Preesens on säännöllinen (<Hv l="ilagon">ilza</Hv>), mutta futuurin ja imperfektin vartalo on *ul-*:
<Hv>unna</Hv>, <Hv>ulza</Hv>, <Hv>ulen</Hv>. Daemon käyttää sitä kolmannella kaudella kahdesti:

<Line id="hotd-s3e05-010" />

<Interlinear hv="Dōrior vēttīlaksir ul|za, otāpt|ā?" gloss="ei.mikään-NOM kiista-NOM olla-FUT.3SG luulla-PRF.2SG" fi="Luulitko, ettei kiistaa tulisi?" />

Repliikin lopussa on <Hv glossed>va mōriot avy dōrurisi lī ulzi</Hv> ’aina on niitä, jotka vastustavat sinua’ –
<Hv glossed>ulzi</Hv> on verbin <Hv>ilagon</Hv> futuurin monikon 3. persoona.

<Paradigm lemma="ilagon" filter="indicative.active.future" />

Samaa *ul*-vartaloa käyttävät myös <Hv>ilagon</Hv>-verbistä johdetut verbit, kuten Duolingosta tuttu
<Hv glossed>bēvilagon</Hv> ’täytyä’: futuuri <Hv glossed>bēvulza</Hv>, imperfekti <Hv glossed>bēvules</Hv>. Aemond
veljelleen, kuningas Aegonille (”sinun piti hoitaa tärkeämpiä asioita”):

<Line id="hotd-s2e04-008" />

### *emagon* ’omistaa’ ja sen johdokset

Preesensin 3. persoona on <Hv>ēza</Hv> / <Hv>ēzi</Hv> (taivutussivun mukaan vanhasta *ems* →
*ēza*), perfektivartalo <Hv>ēd-</Hv>. Kaikki *-emagon*-johdokset perivät saman perfektin. Punaisen papittaren
saarnassa Daenerysista sanotaan:

<Line id="got-s5e03-009" />

<Hv glossed>Dāerēdas</Hv> ’on vapauttanut’ < <Hv>dāeremagon</Hv>. Näihin johdoksiin palataan <LessonLink n={45}>oppitunnilla 45</LessonLink>.

### *sahagon* ’pakottaa, saada tekemään’

Duolingosta tuttu kausatiiviverbi on kauttaaltaan epäsäännöllinen: preesens <Hv fi="pakotan">sān</Hv>, <Hv fi="pakotat">sā</Hv>,
<Hv l="sahagon" fi="pakottaa">sas</Hv>, <Hv fi="pakotamme">sē</Hv>, <Hv fi="pakotatte">sāt</Hv>, <Hv fi="pakottavat">sēs</Hv>; futuuri <Hv fi="tulen pakottamaan">sīnna</Hv>; perfekti
<Hv fi="pakotin">sētan</Hv>. Rhaenyra:

<Line id="hotd-s3e05-007" />

<Interlinear hv="… yn jaehossa dāria sagon ynot sēt|is." gloss="… mutta jumala-NOM.PL kuningatar-NOM olla-INF minä-DAT pakottaa-PRF.3PL" fi="…mutta jumalat itse tekivät minusta kuningattaren." />

Rakenne on Duolingosta tuttu: pakotettava (tässä ”minä”) on datiivissa ja teko infinitiivissä.

<Note>
Rhaenyra käyttää ensimmäisellä kaudella imperatiivia <Hv glossed>sahās</Hv> ’tee (minusta)’, mutta wikin
taivutustaulukossa imperatiivi on <Hv l="sagon">sās</Hv>, joka on sama kuin <Hv glossed>sagon</Hv>-verbin ’ole!’. Repliikki on tässä:

<Line id="hotd-s1e05-008" />

Sarja ja sanakirja ovat tässä ristiriidassa, ja kohta kannattaa liputtaa.
</Note>

### *gaomagon* ’tehdä’

Verbi näyttää säännölliseltä, mutta preesensin 3. persoonassa on rinnakkaismuoto <Hv>gōza</Hv> /
<Hv>gōzi</Hv> (kuten <Hv>ēza</Hv>) ja perfektivartalo on <Hv>gōnt-</Hv>.

<Paradigm lemma="gaomagon" filter="indicative.active.present" />

## Johdokset perivät epäsäännöllisyyden

Tämä on käytännössä tärkein sääntö: verbi, joka on johdettu epäsäännöllisestä verbistä, taipuu
samoin. Muutama esimerkki sarjoista:

- <Hv glossed>henujagon</Hv> ’lähteä’ (< *jagon*): perfekti <Hv glossed>hembistas</Hv> ’on paennut’ – Daenerysin
  tuutulaulussa <Hv glossed>munnon hembistas</Hv> ’suru on paennut’:

<Line id="got-s7e04-001" />

- <Hv glossed>āmāzigon</Hv> ’palata’ (< *māzigon*): <Hv glossed>āmastā</Hv> ’palasit’:

<Line id="hotd-s1e04-002" />

Sääntö ei ole aukoton. <Hv glossed>Siñagon</Hv> ’tulla joksikin’ on johdettu *sagon*-verbistä, mutta se ei
taivu samoin: preesens on säännöllinen (<Hv l="siñagon">siñas</Hv>), ja perfekti on <Hv>sīdas</Hv> eikä *sagon*-verbin
tapaan <Hv l="sagon">istas</Hv>. Katso siis johdoksen taivutus sen omasta taulukosta.

## Harjoituslauseita

<Practice hv="Ēlī Valyriot īnna." fi="Ensin menen Valyriaan." />

<Practice hv="Valyriā ulin." fi="Olimme (silloin) Valyriassa." />

<Practice hv="Rūniapos qurdot ulza." fi="Kirje on (sitten) pöydällä." />

<Practice hv="Skoros gōza?" fi="Mitä hän tekee?" />

## Sanasto

<Vocab />

## Tarkistus

<Check />
